/**
 * Synchronous session hint.
 *
 * The auth context needs an async round-trip before it can say whether a user
 * is signed in. On a deep link to a public-only URL that delay is visible: the
 * marketing page paints for a frame before the redirect into the app shell.
 *
 * The Supabase client persists its session in localStorage, so we can read a
 * best-effort answer synchronously on first render. This is a UI hint only:
 * never use it for access control (guards still verify with the auth server).
 */

const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined;
const STORAGE_KEY = PROJECT_ID ? `sb-${PROJECT_ID}-auth-token` : "";

/**
 * True when a non-expired, non-anonymous session token is present in storage.
 * Returns false when we cannot tell.
 */
export function hasStoredSession(): boolean {
  if (typeof window === "undefined" || !STORAGE_KEY) return false;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as {
      expires_at?: number;
      user?: { is_anonymous?: boolean };
    } | null;
    if (!parsed) return false;
    if (parsed.user?.is_anonymous) return false;
    if (typeof parsed.expires_at === "number" && parsed.expires_at * 1000 <= Date.now()) {
      // Expired access tokens can still refresh, so treat them as "signed in"
      // for layout purposes: the guard resolves the truth a moment later.
      return true;
    }
    return true;
  } catch {
    return false;
  }
}
